import { Box, Divider, Grid2, IconButton, Stack } from "@mui/material";
import { Player } from "../../../domain/model/User";
import { CustomTextView } from "../../component/CustomTextView";
import { useTranslation } from "react-i18next";
import {
  COLOR_BLACK,
  COLOR_LIGHT_GRAY,
  COLOR_MAIN,
  COLOR_VERY_LIGHT_GRAY,
  COLOR_WHITE,
} from "../../../data/colors/Colors";
import { CustomTextField } from "../../component/CustomTextField";
import {
  CardGiftcardRounded,
  CloseRounded,
  GamepadRounded,
  GamesRounded,
  GifRounded,
  SportsEsportsRounded,
} from "@mui/icons-material";
import { CustomCardView } from "../../component/CustomCardView";
import { ChoosePackageViewModel } from "./ChoosePackageViewModel";
import {
  provideCheckDiscountCodeIfRedeemableUseCase,
  provideGetAllPackagesUseCase,
  provideRedeemDicountCodeUseCase,
} from "../../di/UseCasesModule";
import { LoadingView } from "../../component/LoadingView";
import { EmptyView } from "../../component/EmptyView";
import { Package } from "../../../domain/model/Package";
import { CustomButtonView } from "../../component/CustomButtonView";
import { CustomPopoverView } from "../../component/CustomPopoverView";
import { useState } from "react";
import { PaymentView } from "./payment/PaymentView";

export function ChoosePackageView({
  player,
  handleClose,
}: {
  player: Player;
  handleClose: () => void;
}) {
  const viewModel = ChoosePackageViewModel(
    provideGetAllPackagesUseCase(),
    provideCheckDiscountCodeIfRedeemableUseCase(),
    provideRedeemDicountCodeUseCase()
  );
  const { t } = useTranslation();

  const [selectedPackage, setSelectedPackage] = useState<Package | undefined>();

  const [anchorElPosition, setAnchorElPosition] = useState<{
    top: number;
    left: number;
  } | null>(null);
  return (
    <Box height={"100%"}>
      {selectedPackage && (
        <CustomPopoverView
          onClose={() => {
            setAnchorElPosition(null);
          }}
          anchorElPosition={anchorElPosition}
          layout={
            <PaymentView
              player={player}
              packageItem={selectedPackage}
              handleClose={() => setAnchorElPosition(null)}
            />
          }
        />
      )}
      <Stack width={"100%"} gap={1} p={1} height={"100%"} display={"flex"}>
        <Stack flex={1}>
          <CustomCardView
            width={"100%"}
            height={"100%"}
            borderRadius={4}
            background={COLOR_MAIN}
            padding={2}
            children={
              <Stack
                width={"100%"}
                direction={"row"}
                alignItems={"center"}
                height={"100%"}
              >
                <Stack width={"100%"}>
                  <Stack alignItems={"center"} direction={"row"} gap={1}>
                    <SportsEsportsRounded
                      sx={{ color: COLOR_WHITE }}
                      fontSize="large"
                    />
                    <CustomTextView
                      text={t("buy_more_games")}
                      variant="h5"
                      fontWeight="bold"
                      textColor={COLOR_WHITE}
                    />
                  </Stack>
                  <Stack alignItems={"center"} direction={"row"} gap={1}>
                    <CustomTextView
                      text={t("you_have")}
                      variant="body1"
                      textColor={COLOR_LIGHT_GRAY}
                    />
                    <CustomTextView
                      text={player.gamesLeftToPlay?.toString()}
                      variant="h5"
                      fontWeight="bold"
                      textColor={COLOR_WHITE}
                    />
                    <CustomTextView
                      text={t("games_left_to_play")}
                      variant="body1"
                      textColor={COLOR_LIGHT_GRAY}
                    />
                  </Stack>
                </Stack>

                <IconButton
                  onClick={handleClose}
                  sx={{
                    width: 40,
                    height: 40,
                    background: "rgba(135,40,77,1)",
                  }}
                >
                  <CloseRounded sx={{ color: "white" }} />
                </IconButton>
              </Stack>
            }
          />
        </Stack>
        <Stack flex={1}>
          <CustomCardView
            width={"100%"}
            borderRadius={4}
            height={"100%"}
            padding={2}
            children={
              <Box
                component={"form"}
                onSubmit={(e) => {
                  e.preventDefault();
                  viewModel.checkIfFreeGameCodeRedeemable();
                }}
                height={"100%"}
              >
                <Stack width={"100%"} gap={1} height={"100%"}>
                  <CustomTextView
                    text={t("free_game_code")}
                    variant="body1"
                    fontWeight="bold"
                  />
                  <Stack
                    width={"100%"}
                    direction={"row"}
                    gap={2}
                    alignItems={"center"}
                  >
                    <Stack width={"100%"}>
                      <CustomTextField
                        placeholder={t("free_game_code")}
                        onTextChange={(t) => {
                          viewModel.setDiscountCode(t);
                        }}
                        required
                        value={viewModel.discountCode}
                        startIcon={
                          <CardGiftcardRounded sx={{ color: COLOR_BLACK }} />
                        }
                      />
                    </Stack>
                    <IconButton
                      sx={{ border: "solid 1px black", p: 2 }}
                      type="submit"
                    >
                      <CardGiftcardRounded sx={{ color: COLOR_BLACK }} />
                    </IconButton>
                  </Stack>
                </Stack>
              </Box>
            }
          />
        </Stack>

        <Stack flex={6}>
          <CustomCardView
            width={"100%"}
            borderRadius={4}
            background={COLOR_MAIN}
            padding={2}
            height={"100%"}
            children={
              <Stack width={"100%"} gap={2} height={"100%"}>
                <Stack width={"100%"}>
                  <CustomTextView
                    text={t("choose_your_right_plan")}
                    variant="h6"
                    fontWeight="bold"
                    textColor={COLOR_WHITE}
                  />

                  <CustomTextView
                    text={t("go_with_plan_fits_best")}
                    variant="body1"
                    textColor={COLOR_LIGHT_GRAY}
                  />
                </Stack>

                <Stack width={"100%"} height={"100%"} justifyContent={"center"}>
                  {viewModel.loading ? (
                    <LoadingView minHeight="10vh" color={COLOR_WHITE} />
                  ) : (
                    <>
                      {viewModel.packages && viewModel.packages.length > 0 ? (
                        <Grid2 container spacing={1}>
                          {viewModel.packages.map((packageItem, index) => {
                            return (
                              <Grid2 size={6} key={packageItem._id}>
                                <PackageItemView
                                  packageItem={packageItem}
                                  index={index}
                                  onPackageClicked={(e) => {
                                    setSelectedPackage(packageItem);
                                    setAnchorElPosition({
                                      top: e.clientY,
                                      left: e.clientX,
                                    });
                                  }}
                                />
                              </Grid2>
                            );
                          })}
                        </Grid2>
                      ) : (
                        <EmptyView minHeight={"10vh"} color={COLOR_WHITE} />
                      )}
                    </>
                  )}
                </Stack>
              </Stack>
            }
          />
        </Stack>
      </Stack>
    </Box>
  );
}
function PackageItemView({
  packageItem,
  index,
  onPackageClicked,
}: {
  packageItem: Package;
  index: number;
  onPackageClicked: (e: React.MouseEvent<HTMLButtonElement | Element>) => void;
}) {
  const { t } = useTranslation();

  const savePercentageText =
    index == 1
      ? t("save_n_percent", { percentage: "5" })
      : index == 2
      ? t("save_n_percent", { percentage: "10" })
      : t("save_n_percent", { percentage: "15" });
  return (
    <CustomCardView
      padding={1}
      borderRadius={4}
      background={COLOR_WHITE}
      children={
        <Stack
          width={"100%"}
          alignItems={"center"}
          justifyContent={"space-between"}
          height={220}
        >
          <Stack
            direction={"row"}
            alignItems={"center"}
            justifyContent={"center"}
            width={"100%"}
            gap={1}
          >
            <CustomTextView
              text={packageItem.numberOfGames?.toString()}
              fontWeight="bold"
              variant="h5"
            />
            <CustomTextView
              text={t(
                packageItem.numberOfGames! > 2
                  ? "games_to_buy"
                  : packageItem.numberOfGames! > 1
                  ? "two_games"
                  : "game"
              )}
              variant="body1"
            />
          </Stack>

          <Stack width={"100%"} alignItems={"center"}>
            {(index == 1 || index == 2 || index == 3) && (
              <CustomTextView
                text={savePercentageText}
                variant="body1"
                textColor={COLOR_LIGHT_GRAY}
              />
            )}
            <CustomTextView
              text={`$${packageItem.priceInUSD}`}
              fontWeight="bold"
              variant="h4"
            />
          </Stack>
          <Stack width={"100%"}>
            <CustomButtonView text={t("purchase")} onClick={onPackageClicked} />
          </Stack>
        </Stack>
      }
    />
  );
}
